import {
  useCallback,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { FloatingMenu, type FloatingMenuPlacement } from './FloatingMenu'
import { TextInput } from './TextInput'
import { Pressable } from './Pressable'
import { getNlsString } from '../../nls/nlsRuntime'
import { COLOR_PICKER_SWATCHES } from './colorPickerConstants'

const COLS = 8

/* ---------------------------------------------------------------------------
 * Helpers
 * ---------------------------------------------------------------------------*/

/** Normalise 3- or 6-digit hex to lowercase #rrggbb, or null on invalid. */
function normaliseHex(raw: string): string | null {
  const s = raw.trim().replace(/^#/, '')
  if (/^[0-9a-fA-F]{6}$/.test(s)) return `#${s.toLowerCase()}`
  if (/^[0-9a-fA-F]{3}$/.test(s)) {
    const [r, g, b] = s.split('')
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }
  return null
}

/** Display as uppercase, no hash (used in input value). */
function toHexDisplay(lc: string): string {
  return lc.replace('#', '').toUpperCase()
}

/* ---------------------------------------------------------------------------
 * Props
 * ---------------------------------------------------------------------------*/

export type ColorPickerProps = {
  value: string
  onChange: (hex: string) => void
  disabled?: boolean
  ariaLabel?: string
  className?: string
  size?: 'sm' | 'md'
  placement?: FloatingMenuPlacement
}

/* ---------------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------------------*/

export function ColorPicker({
  value,
  onChange,
  disabled = false,
  ariaLabel,
  className,
  size,
  placement = 'bottom-start',
}: ColorPickerProps) {
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)

  // Active swatch index for roving tabIndex
  const [activeIdx, setActiveIdx] = useState<number>(() => {
    const i = COLOR_PICKER_SWATCHES.indexOf(value.toLowerCase())
    return i >= 0 ? i : 0
  })

  // Hex field: mirrors value while closed; editable while open
  const [hexField, setHexField] = useState(toHexDisplay(value))
  const [hexError, setHexError] = useState(false)

  const handleOpen = useCallback(() => {
    if (disabled) return
    setHexField(toHexDisplay(value))
    setHexError(false)
    const i = COLOR_PICKER_SWATCHES.indexOf(value.toLowerCase())
    setActiveIdx(i >= 0 ? i : 0)
    setOpen(true)
  }, [disabled, value])

  const handleClose = useCallback(() => {
    setOpen(false)
    setHexError(false)
  }, [])

  const handleToggle = useCallback(() => {
    if (open) handleClose()
    else handleOpen()
  }, [open, handleClose, handleOpen])

  /** Emit a swatch colour and close. */
  const pickSwatch = useCallback(
    (hex: string, idx: number) => {
      setActiveIdx(idx)
      setHexField(toHexDisplay(hex))
      setHexError(false)
      onChange(hex)
      handleClose()
    },
    [onChange, handleClose],
  )

  /** Arrow key navigation in the swatch grid (roving tabIndex grid). */
  const handleGridKey = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      const total = COLOR_PICKER_SWATCHES.length
      let next: number
      if (e.key === 'ArrowRight') next = (activeIdx + 1) % total
      else if (e.key === 'ArrowLeft') next = (activeIdx - 1 + total) % total
      else if (e.key === 'ArrowDown') next = Math.min(activeIdx + COLS, total - 1)
      else if (e.key === 'ArrowUp') next = Math.max(activeIdx - COLS, 0)
      else return
      e.preventDefault()
      setActiveIdx(next)
      // Focus the newly active button
      const grid = e.currentTarget
      const btn = grid.querySelectorAll<HTMLButtonElement>('.dsSwatch')[next]
      btn?.focus()
    },
    [activeIdx],
  )

  /** Commit the hex field value (Enter or blur). */
  const commitHex = useCallback(() => {
    const norm = normaliseHex(hexField)
    if (!norm) {
      setHexError(true)
      return
    }
    setHexError(false)
    onChange(norm)
    setHexField(toHexDisplay(norm))
    // Don't close on blur in case user wants to keep editing; they can press Esc.
    // But we do close on Enter (handled below).
  }, [hexField, onChange])

  const handleHexKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const norm = normaliseHex(hexField)
        if (!norm) {
          setHexError(true)
          return
        }
        setHexError(false)
        onChange(norm)
        setHexField(toHexDisplay(norm))
        handleClose()
      }
    },
    [hexField, onChange, handleClose],
  )

  // Display value in trigger (uppercase)
  const displayHex = value ? `#${value.replace('#', '').toUpperCase()}` : '#------'

  return (
    <>
      <Pressable
        ref={anchorRef}
        className={[
          'dsDatePicker__trigger',
          size === 'sm' ? 'dsDatePicker__trigger--sm' : '',
          open ? 'is-open' : '',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        disabled={disabled}
        aria-disabled={disabled || undefined}
        aria-label={ariaLabel ?? displayHex}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={handleToggle}
      >
        <span className="dsSwatchDot" style={{ background: value }} aria-hidden="true" />
        <span className="dsColorPicker__hex">{displayHex}</span>
      </Pressable>

      <FloatingMenu
        anchorRef={anchorRef}
        isOpen={open}
        onClose={handleClose}
        placement={placement}
        className="dsDrop dsDrop--pad"
        role="dialog"
        ariaLabel={ariaLabel ?? getNlsString('NLSCommonColorPickerHexLabel')}
      >
        {/* Swatch grid — single Tab stop via roving tabIndex */}
        <div
          className="dsSwatches"
          role="group"
          onKeyDown={handleGridKey}
        >
          {COLOR_PICKER_SWATCHES.map((hex, i) => (
            <Pressable
              key={hex}
              className={['dsSwatch', value.toLowerCase() === hex ? 'is-selected' : ''].filter(Boolean).join(' ')}
              style={{ background: hex }}
              aria-label={hex}
              tabIndex={i === activeIdx ? 0 : -1}
              onClick={() => pickSwatch(hex, i)}
            />
          ))}
        </div>

        {/* Hex field */}
        <TextInput
          size="sm"
          leadingIcon="#"
          value={hexField}
          onChange={(v) => {
            setHexField(v)
            setHexError(false)
          }}
          onBlur={commitHex}
          onKeyDown={handleHexKeyDown}
          error={hexError}
          aria-label={getNlsString('NLSCommonColorPickerHexLabel')}
          aria-invalid={hexError || undefined}
          aria-describedby={hexError ? 'cp-hex-err' : undefined}
          maxLength={6}
          autoComplete="off"
          spellCheck={false}
        />
        {hexError && (
          <span id="cp-hex-err" role="alert" style={{ fontSize: '11px', color: 'var(--danger)' }}>
            {getNlsString('NLSCommonColorPickerInvalidColor')}
          </span>
        )}
      </FloatingMenu>
    </>
  )
}
